'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconPhone } from '@tabler/icons-react'
import { object, ref, string } from 'yup'

import Input from 'shared/ui/form/Input'
import { validateField, validateValues } from 'shared/utils/form'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { useCustomerData } from '../hooks/useCustomerData'

import ProfileDetailsSingleItem from './ProfileDetailsSingleItem'

interface FormPayload {
  phoneNumber: string
}

const schema = object().shape({
  phoneNumber: string().test(
    'is-present-or-valid',
    'Numer telefonu jest wymagany lub ma nieprawidłowy format',
    function (value) {
      const { path, createError } = this
      const phoneNumber = this.resolve(ref(path))

      if (isNilOrEmpty(phoneNumber) && isNilOrEmpty(value)) {
        return createError({ path, message: 'Numer telefonu jest wymagany' })
      }

      if (value && !/^\d{9}$/.test(value)) {
        return createError({ path, message: 'Nieprawidłowy format' })
      }

      return true
    }
  )
})

const validateFormField = (values: FormPayload) => validateField(schema, values)
const validateFormValues = validateValues(schema)

const initialValues = {
  phoneNumber: ''
}

const CustomerPhoneNumberUpdateForm = () => {
  const [values, setValues] = useState(initialValues)
  const [valid, setValid] = useState(false)
  const { user } = useCustomerData()

  // Memoize the validation function to prevent infinite re-renders
  const memoizedValidateFormField = useMemo(() => {
    return validateFormField(values)
  }, [values])

  const getInitialData = () => {
    if (user) {
      setValues({
        phoneNumber: user.phoneNumber
      })
    }
  }

  useEffect(() => {
    getInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    values && validateFormValues(values, setValid)
  }, [values])

  return (
    <ProfileDetailsSingleItem
      id='profile_edit_phone'
      modalTitle='Edytuj numer telefonu'
      isSubmitDisabled={!valid}
      icon={<IconPhone />}
      label='Numer telefonu'
      value={user?.phoneNumber || '---'}
      values={values}
      openCallback={getInitialData}
    >
      <Input
        id='phone_update_new_number'
        size='medium'
        name='phoneNumber'
        type='tel'
        required
        icons={{
          start: <span aria-hidden='true'>+48</span>
        }}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'tel-national',
          'aria-describedby': 'phone_update_format'
        }}
        label='Numer telefonu'
        value={values.phoneNumber}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
      <span id='phone_update_format' className='visually-hidden'>
        9 cyfr, bez numeru kierunkowego +48
      </span>
    </ProfileDetailsSingleItem>
  )
}

export default CustomerPhoneNumberUpdateForm
